import { supabase } from "@/integrations/supabase/client";

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export type ThreadPurpose = 'general' | 'call_creation' | 'kb_work' | 'team_collaboration';
export type GibletBackend = 'lovable-ai' | 'openai-direct';

interface GibletApiResponse {
  response: string;
  conversationId?: string;
  citations?: Array<{
    kbTitle: string;
    kbDescription?: string;
    snippet: string;
    relevance: number;
  }>;
  backend?: string;
  model?: string;
}

export const callGibletApi = async (
  message: string, 
  userContext?: string,
  conversationHistory?: Message[],
  connectedKBIds?: string[],
  workflowState?: Record<string, any>,
  conversationId?: string,
  threadPurpose?: ThreadPurpose,
  backend: GibletBackend = 'lovable-ai'
): Promise<GibletApiResponse> => {
  try {
    // Determine which edge function to use based on backend choice and userContext
    let functionName: string;
    
    if (backend === 'openai-direct') {
      functionName = 'giblet-chat-openai';
      console.log('Using OpenAI direct backend');
    } else {
      // Default Lovable AI backend - check for CURT channel
      const isCurtChannel = userContext?.includes('CURT Channel');
      functionName = isCurtChannel ? 'giblet-construct-chat' : 'giblet-chat';
    }
    
    console.log(`Routing to ${functionName}:`, { backend, threadPurpose });

    const { data, error } = await supabase.functions.invoke(functionName, {
      body: { 
        message,
        userContext,
        conversationHistory,
        connectedKBIds,
        workflowState,
        conversationId,
        threadPurpose: threadPurpose || 'general'
      }
    });

    if (error) {
      console.error(`Error invoking ${functionName}:`, error);
      
      // Handle authentication errors
      if (error.message?.includes('JWT') || error.message?.includes('auth')) {
        return { response: "Authentication error. Please try logging out and back in." };
      }
      
      // Handle CORS errors
      if (error.message?.includes('CORS')) {
        return { response: "Connection error. Please refresh the page and try again." };
      }
      
      throw error;
    }

    if (data?.error) {
      console.error(`Error from ${functionName}:`, data.error);
      
      // Handle AI API errors
      if (data.error.includes('high demand') || data.error.includes('Rate limit')) {
        return { response: "I'm currently experiencing high demand. Please try again in a moment." };
      }
      
      if (data.error.includes('credits exhausted')) {
        return { response: "AI service credits exhausted. Please contact your administrator to add credits." };
      }
      
      if (data.error.includes('Authentication failed') || data.error.includes('unavailable')) {
        return { response: "The AI service is currently unavailable. Please contact your administrator." };
      }
      
      if (data.error.includes('API_KEY is not configured')) {
        return { response: "AI service is not properly configured. Please contact your administrator." };
      }
      
      throw new Error(data.error);
    }

    return {
      response: data?.response || "I apologize, but I couldn't generate a response. Please try again.",
      conversationId: data?.conversationId,
      citations: data?.citations,
      backend: data?.backend,
      model: data?.model
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    console.error('Error in callGibletApi:', errorMessage);
    
    // Network errors
    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
      return { response: "I'm having trouble connecting to the server. Please check your internet connection and try again." };
    }
    
    // Function not found errors
    if (errorMessage.includes('not found') || errorMessage.includes('FunctionsRelayError')) {
      return { response: "The AI service is temporarily unavailable. Please try again in a moment." };
    }
    
    return { response: `I encountered an error: ${errorMessage}. Please try again later.` };
  }
};
